// src/services/fieldSubmissionService.js
/**
 * Field Submission Service
 *
 * Data fetching untuk SubmitProgressModal dan UploadEvidenceModal.
 * Handles scope (segment/site) dan milestone selection data.
 *
 * Role-based data sources:
 *   Supervisor → GET /api/field-supervisors/{userId} → assigned segments/sites only
 *   PM/PC      → GET /api/fo-segments & /api/tower-sites → semua data
 */

import api from '../config/axios';
import { getUserId, isFieldWorker } from '../components/auth/authUtils';

const SUPERVISOR_API = '/field-supervisors';
const SEGMENTS_API   = '/fo-segments';
const SITES_API      = '/tower-sites';

// ============================================================================
// SCOPE DATA (Step 1)
// ============================================================================

/**
 * Get FO Segments available for the current user.
 *
 * Supervisor → hanya segment yang di-assign ke user ini
 *              via GET /api/field-supervisors/{userId}
 * PM/PC      → semua segment via GET /api/fo-segments
 *
 * Returns normalized array:
 *   [{ id, name, code, projectName, projectId, scopeType: 'FO' }]
 */
export const getAvailableSegments = async () => {
  if (isFieldWorker()) {
    const userId = getUserId();
    const res    = await api.get(`${SUPERVISOR_API}/${userId}`);
    const data   = res.data;

    return (data.assignedSegments ?? []).map(seg => ({
      id:          seg.id,
      name:        seg.siteNameAB,
      code:        seg.spanId,
      segmentCode: seg.segmentId,
      projectName: seg.projectName,
      projectId:   seg.projectId,
      status:      seg.status,
      progress:    seg.progressPercentage,
      scopeType:   'FO',
    }));
  }

  // PM/PC: semua segment
  const res  = await api.get(SEGMENTS_API);
  const list = Array.isArray(res.data) ? res.data
             : (res.data?.content ?? res.data?.data ?? []);

  return list.map(seg => ({
    id:          seg.id,
    name:        seg.siteNameAB ?? seg.siteName ?? seg.name,
    code:        seg.spanId     ?? seg.code,
    segmentCode: seg.segmentId,
    projectName: seg.project?.projectName ?? seg.projectName,
    projectId:   seg.project?.id         ?? seg.projectId,
    status:      seg.status,
    progress:    seg.progressPercentage,
    scopeType:   'FO',
  }));
};

/**
 * Get Tower Sites available for the current user.
 * Same logic as getAvailableSegments but for Tower.
 *
 * Returns normalized array:
 *   [{ id, name, code, projectName, projectId, scopeType: 'TOWER' }]
 */
export const getAvailableSites = async () => {
  if (isFieldWorker()) {
    const userId = getUserId();
    const res    = await api.get(`${SUPERVISOR_API}/${userId}`);
    const data   = res.data;

    return (data.assignedSites ?? []).map(site => ({
      id:          site.id,
      name:        site.siteName,
      code:        site.siteId,
      projectName: site.projectName,
      projectId:   site.projectId,
      regional:    site.regional,
      status:      site.status,
      progress:    site.progressPercentage,
      scopeType:   'TOWER',
    }));
  }

  // PM/PC: semua site
  const res  = await api.get(SITES_API);
  const list = Array.isArray(res.data) ? res.data
             : (res.data?.content ?? res.data?.data ?? []);

  return list.map(site => ({
    id:          site.id,
    name:        site.siteName ?? site.name,
    code:        site.siteId   ?? site.code,
    projectName: site.project?.projectName ?? site.projectName,
    projectId:   site.project?.id         ?? site.projectId,
    regional:    site.regional,
    status:      site.status,
    progress:    site.progressPercentage,
    scopeType:   'TOWER',
  }));
};

// ============================================================================
// MILESTONE DATA (Step 2)
// ============================================================================

/**
 * Get leaf milestones for an FO Segment.
 * Filters out group milestones (isGroup = true) — those are containers only.
 *
 * GET /api/segments/{segmentId}/milestones
 *
 * Returns normalized array:
 *   [{ id, itemCode, name, isGroup, finalProgress, status, planStart, planFinish }]
 */
export const getLeafMilestonesBySegment = async (segmentId) => {
  const res  = await api.get(`/segments/${segmentId}/milestones`);
  const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

  return list
    .filter(m => !m.isGroup)
    .map(normalizeMilestone);
};

/**
 * Get leaf milestones for a Tower Site.
 *
 * GET /api/sites/{siteId}/milestones
 */
export const getLeafMilestonesBySite = async (siteId) => {
  const res  = await api.get(`/sites/${siteId}/milestones`);
  const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

  return list
    .filter(m => !m.isGroup)
    .map(normalizeMilestone);
};

const normalizeMilestone = (m) => ({
  id:           m.id,
  itemCode:     m.itemCode,
  name:         m.milestoneName ?? m.name,
  isGroup:      m.isGroup ?? false,
  finalProgress: m.finalProgress ?? 0,
  status:       m.status ?? 'NOT_STARTED',
  planStart:    m.planStartDate,
  planFinish:   m.planFinishDate,
  calculationMethod: m.calculationMethod,
  volumeUnit:   m.volumeUnit,
});

// ============================================================================
// EVIDENCE DEFINITIONS (Step 3 — Upload)
// ============================================================================

/**
 * Get evidence definitions for a milestone.
 * Digunakan untuk populate dropdown tipe evidence di Step 3.
 *
 * GET /api/milestones/{milestoneId}/evidence/requirements
 *
 * Returns definitions dengan status upload per definition.
 */
export const getEvidenceDefinitions = async (milestoneId) => {
  const res  = await api.get(`/milestones/${milestoneId}/evidence/requirements`);
  const list = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

  return list
    .filter(def => def.minFileCount !== null) // skip GROUP containers
    .map(def => ({
      id:               def.id,
      name:             def.evidenceName,
      evidenceType:     def.evidenceType,
      isMandatory:      def.isMandatory,
      minFileCount:     def.minFileCount,
      maxFileCount:     def.maxFileCount,
      description:      def.description,
      allowedFileTypes: def.allowedFileTypes,
      maxFileSizeMb:    def.maxFileSizeMb,
      uploadedCount:    def.uploadedFileCount ?? 0,
      requirementMet:   def.isRequirementMet ?? false,
      requirementText:  def.fileCountRequirementText,
    }));
};

// ============================================================================
// SUBMIT ACTIONS
// ============================================================================

/**
 * Submit progress for a milestone (web endpoint).
 *
 * POST /api/milestones/{milestoneId}/progress
 *
 * @param {number} milestoneId
 * @param {{ percentage, status, workDescription, latitude?, longitude? }} data
 */
export const submitProgress = async (milestoneId, data) => {
  const payload = {
    milestoneId,
    submittedProgress: data.percentage,
    workDescription:   data.notes,
    progressDate:      new Date().toISOString().split('T')[0],
    submissionSource:  'WEB_APP',
  };

  if (data.latitude  != null) payload.latitude  = data.latitude;
  if (data.longitude != null) payload.longitude = data.longitude;

  const res = await api.post(`/milestones/${milestoneId}/progress`, payload);
  return res.data;
};

/**
 * Upload evidence to a milestone (web, decoupled).
 *
 * POST /api/milestones/{milestoneId}/evidence/upload
 * Content-Type: multipart/form-data
 *
 * @param {number} milestoneId
 * @param {File}   file
 * @param {{ evidenceDefinitionId?, caption? }} options
 */
export const uploadEvidence = async (milestoneId, file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  if (options.evidenceDefinitionId != null) {
    formData.append('evidenceDefinitionId', options.evidenceDefinitionId);
  }
  if (options.caption) {
    formData.append('caption', options.caption);
  }

  const res = await api.post(
    `/milestones/${milestoneId}/evidence/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return res.data;
};

// ============================================================================
// HELPERS
// ============================================================================

export const getMilestoneStatusLabel = (status) => ({
  NOT_STARTED: 'Belum mulai',
  IN_PROGRESS: 'Berjalan',
  COMPLETED:   'Selesai',
  ON_HOLD:     'Ditunda',
}[status] ?? status);

export const getMilestoneStatusColor = (status) => ({
  NOT_STARTED: 'text-gray-400',
  IN_PROGRESS: 'text-blue-600',
  COMPLETED:   'text-emerald-600',
  ON_HOLD:     'text-amber-600',
}[status] ?? 'text-gray-400');

export const getMilestoneStatusBg = (status) => ({
  NOT_STARTED: 'bg-gray-100 text-gray-600 border-gray-200',
  IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
  COMPLETED:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  ON_HOLD:     'bg-amber-50 text-amber-700 border-amber-200',
}[status] ?? 'bg-gray-100 text-gray-600 border-gray-200');

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const ALLOWED_DOC_TYPES   = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024)  return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const validateFile = (file, definition = null) => {
  const MAX_SIZE_MB  = definition?.maxFileSizeMb ?? 10;
  const MAX_SIZE     = MAX_SIZE_MB * 1024 * 1024;

  if (file.size > MAX_SIZE) {
    return `File terlalu besar. Maksimal ${MAX_SIZE_MB}MB`;
  }

  if (definition?.allowedFileTypes?.length) {
    const ext = file.name.toLowerCase().split('.').pop();
    if (!definition.allowedFileTypes.includes(ext)) {
      return `Tipe file tidak diizinkan. Allowed: ${definition.allowedFileTypes.join(', ')}`;
    }
  }

  return null; // valid
};

export default {
  getAvailableSegments,
  getAvailableSites,
  getLeafMilestonesBySegment,
  getLeafMilestonesBySite,
  getEvidenceDefinitions,
  submitProgress,
  uploadEvidence,
  getMilestoneStatusLabel,
  getMilestoneStatusColor,
  getMilestoneStatusBg,
  formatFileSize,
  validateFile,
};